import { Link } from '@tanstack/react-router';
import { CopyIcon, DownloadIcon, FolderOpenIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button, buttonVariants } from '@/components/ui/button';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { FileChangeEventView, FileEntry } from '@/lib/api/generated/model';
import { filePreviewLink } from '../filePreviewLink';
import { formatExactTime, formatFileSize, formatRelativeTime } from '../format';
import { useDownloadWorkspaceFile } from '../hooks/useDownloadWorkspaceFile';
import { useWorkspaceFileHistory } from '../hooks/useWorkspaceFileHistory';
import type { FileView } from '../searchParams';
import { transitionName } from '../transitionName';
import { FILE_VIEW_PANEL_ID, viewTabId } from '../viewIds';
import { ActorLabel } from './ActorLabel';

const EVENT_TYPE_LABEL: Record<FileChangeEventView['event_type'], string> = {
  created: 'Created',
  modified: 'Modified',
  deleted: 'Deleted',
  moved: 'Moved',
};

const ALL_FILE_VIEWS: readonly FileView[] = ['preview', 'history'];

/**
 * What the reader is looking at, and what they can do with it.
 *
 * The preview used to open straight into content: no name — the only copy of
 * it was in a 28rem listing column that truncates — no size, no provenance, no
 * way back to the full-width listing, and a download only if the file happened
 * to be an image or a binary.
 */
export function PreviewHeader({
  workspaceId,
  entry,
  views = ALL_FILE_VIEWS,
  view,
  onViewChange,
  onClose,
  closeLabel = 'Close file',
  openInFiles = false,
}: {
  workspaceId: string;
  entry: FileEntry;
  /** Which views to offer. Defaults to both; a single view draws no strip. */
  views?: readonly FileView[];
  view: FileView;
  onViewChange: (view: FileView) => void;
  onClose: () => void;
  /**
   * The accessible name of the one close control in this header.
   *
   * Defaults to `Close file`, which is the Files page's own wording and must
   * not change (FR-047a, SC-011). The preview panel passes
   * `Close the file preview` instead, because in the panel this header's close
   * **is** the panel's close control (FR-047): the panel used to draw a second
   * X of its own above the header, and two X buttons a few pixels apart read
   * as one of them being broken.
   */
  closeLabel?: string;
  /**
   * Offer "Open in Files": the Files page with this file selected, its folder
   * listed and its History a tab away (spec 250).
   *
   * The preview panel beside a task passes it. A path in a run's results opens
   * that panel in place, which keeps the task in view but left no way on to
   * the file's own page — finding it there meant walking the folders by hand.
   * The Files page leaves it off: the reader is already there.
   */
  openInFiles?: boolean;
}) {
  const { items, total } = useWorkspaceFileHistory(workspaceId, entry.path);
  const latest = items[0];
  const { download } = useDownloadWorkspaceFile(workspaceId);

  return (
    // A container: the header is as wide as the pane it heads, not the window.
    // The preview panel beside a task is often narrower than its `24rem` floor
    // allows, and then the labels go before the controls do (spec 250).
    <div className="border-border @container flex flex-none flex-col gap-2 border-b px-3 py-2">
      {/* On a phone a grid: the name beside the buttons, the size and last
          change on a full-width line under both. Beside three 44px buttons
          that line lost who made the change (spec 272). */}
      <div className="flex items-start justify-between gap-3 max-md:grid max-md:grid-cols-[minmax(0,1fr)_auto] max-md:gap-x-3 max-md:gap-y-0">
        <div className="flex min-w-0 flex-col max-md:contents">
          <p
            className="text-foreground truncate text-sm font-medium max-md:self-center"
            title={entry.name}
          >
            {/* The name the row handed over. On an inline span rather than the
                paragraph: a transition morphs boxes, and the paragraph fills
                this column while the text does not — naming the wider box makes
                the morph read as a scale instead of a name moving. */}
            <span style={{ viewTransitionName: transitionName(entry.path) }}>
              {entry.name}
            </span>
          </p>
          <p className="text-muted-foreground flex min-w-0 items-center gap-1 text-xs max-md:col-span-2 max-md:row-start-2">
            <span className="shrink-0">{formatFileSize(entry.size)}</span>
            {latest && <LastChange event={latest} />}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1 pointer-coarse:gap-2 max-md:col-start-2 max-md:row-start-1">
          {openInFiles && (
            // A link, not a button that navigates: the destination is in the
            // href, so it opens in a new tab too.
            <Link
              {...filePreviewLink(workspaceId, entry.path)}
              className={buttonVariants({ variant: 'ghost', size: 'sm' })}
              title="Open in Files"
            >
              <FolderOpenIcon aria-hidden="true" />
              {/* Left of Download, so in a narrow panel it is the first thing
                  the card's overflow would cut: an icon there, not a word. */}
              <span className="@max-[32rem]:sr-only">Open in Files</span>
            </Link>
          )}
          <Button
            variant="ghost"
            size="sm"
            // An icon alone on a phone: still 44 wide on touch (spec 272).
            className="pointer-coarse:min-w-11"
            title="Download"
            onClick={() => {
              void download(entry.path, entry.name).catch(() => {
                toast.error('The file could not be downloaded.');
              });
            }}
          >
            <DownloadIcon aria-hidden="true" />
            {/* An icon beside its two neighbours on a phone, where the word
                left who last changed the file no room (spec 173). */}
            <span className="max-sm:sr-only @max-[32rem]:sr-only">
              Download
            </span>
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Copy path"
            onClick={() => {
              void navigator.clipboard?.writeText(entry.path).then(
                () => {
                  toast.success('Path copied.');
                },
                () => {
                  toast.error('The path could not be copied.');
                },
              );
            }}
          >
            <CopyIcon aria-hidden="true" strokeWidth={1.75} />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={closeLabel}
            onClick={onClose}
          >
            <XIcon aria-hidden="true" strokeWidth={1.75} />
          </Button>
        </div>
      </div>

      {/* Tabs rather than a second panel: history is read occasionally and
          needs width when it is, so it takes the preview's rather than holding
          a permanent rail of its own.

          Drawn flush with the header's own bottom border — the negative
          margins cancel the header's padding — so the active tab's underline
          continues that border rather than floating above it. As small
          rounded pills they read as text, and nothing said "these switch what
          is below".

          Drawn only when there is more than one view to switch between: a
          one-tab tablist is a control that cannot do anything, so a host that
          offers a single view gets no strip at all rather than a decorative
          one. */}
      {views.length > 1 && (
        <TabsList
          aria-label="File views"
          // Two tabs share a phone's width (spec 272).
          className="-mx-3 -mb-2 px-3 max-md:*:flex-1 max-md:*:justify-center"
        >
          {views.includes('preview') && (
            <ViewTab
              value="preview"
              current={view}
              onSelect={onViewChange}
              label="Preview"
            />
          )}
          {views.includes('history') && (
            <ViewTab
              value="history"
              current={view}
              onSelect={onViewChange}
              label="History"
              count={total}
            />
          )}
        </TabsList>
      )}
    </div>
  );
}

function ViewTab({
  value,
  current,
  onSelect,
  label,
  count,
}: {
  value: FileView;
  current: FileView;
  onSelect: (view: FileView) => void;
  label: string;
  count?: number;
}) {
  return (
    // `ui/tabs`, which is this strip's own treatment lifted out of it — the
    // underline and the reasoning above it were right, and the task drawer's
    // pills were the copy that disagreed. What the primitive adds is the arrow-
    // key navigation `role="tablist"` promises and neither strip had.
    <TabsTrigger
      id={viewTabId(value)}
      panelId={FILE_VIEW_PANEL_ID}
      selected={value === current}
      count={count}
      onClick={() => {
        onSelect(value);
      }}
    >
      {label}
    </TabsTrigger>
  );
}

/**
 * The newest change, in one line.
 *
 * "Who last touched this?" is asked constantly and answered in a sentence;
 * "show me the whole log" is asked rarely and needs the width. This answers
 * the first, from the same query the history view reads, so heading the file
 * with its provenance costs no extra request.
 */
function LastChange({ event }: { event: FileChangeEventView }) {
  return (
    <>
      <span aria-hidden="true">·</span>
      <span className="shrink-0" title={formatExactTime(event.occurred_at)}>
        {EVENT_TYPE_LABEL[event.event_type]}{' '}
        {formatRelativeTime(event.occurred_at)} by
      </span>
      {/* The task alone: a line this narrow cut all three names of the full
          form to nothing, down to "by › ›" on a phone (spec 173). History
          names them in full. */}
      <ActorLabel actor={event.actor} detail="task" />
    </>
  );
}
